import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { handleFetchError, translate } from '../../utils';
import useGlobalSpinner from '../../hooks/useGlobalSpinner';
import FilterSelection from '../FilterSelection/FilterSelection';
import { PortfolioDashboardProvider } from './PortfolioDashboardContext';
import { PortfolioAlerts } from './PortfolioAlerts';
import { PortfolioForecast } from './PortfolioForecast';
import { PortfolioHotelRanking } from './PortfolioHotelRanking';
import { PortfolioMetrics } from './PortfolioMetrics';
import { PortfolioPeriodPicker } from './PortfolioPeriodPicker';
import { PortfolioSummary } from './PortfolioSummary';
import { buildSearchParams } from './portfolioDashboardUtilities';

const PROPERTY_FILTER_SUGGESTED_FIELD_NAMES = ['properties'];

/** @param {import('./portfolioDashboard.types').PortfolioDashboardProps} props */
const PortfolioDashboard = function PortfolioDashboard({
    propertiesOptionsForFilter,
    selectedPropertiesKeys: initialSelectedPropertiesKeys,
    selectedPeriod: initialPeriod,
    portfolioData: initialPortfolioData,
    currency,
}) {
    const [period, setPeriod] = useState(initialPeriod);
    const [propertyFilter, setPropertyFilter] = useState(
        initialSelectedPropertiesKeys === null ? {} : { properties: { in: initialSelectedPropertiesKeys } },
    );
    const [portfolioData, setPortfolioData] = useState(initialPortfolioData);
    const [, setIsLoading] = useGlobalSpinner(false);
    const abortControllerRef = useRef(null);

    const propertyFilterSchema = useMemo(() => ({
        properties: {
            label: translate('frontend', 'PORTFOLIO_DASHBOARD_PROPERTIES'),
            type: 'enum',
            values: Object.fromEntries(
                Object.entries(propertiesOptionsForFilter).map(([propertyKey, name]) => [propertyKey, { label: name }]),
            ),
            displayValue: (selectedPropertiesKeys) => translate('frontend', 'FILTER_SELECTION_SELECTED_VALUES', {
                count: selectedPropertiesKeys.length,
            }),
        },
    }), [propertiesOptionsForFilter]);

    const loadPortfolioData = useCallback(async (selectedPropertiesKeys, selectedPeriod) => {
        const queryString = buildSearchParams(selectedPropertiesKeys, selectedPeriod).toString();

        window.history.replaceState(null, '', queryString === '' ? window.location.pathname : `${window.location.pathname}?${queryString}`);

        setIsLoading(true);

        try {
            const response = await fetch(`/portfolio-dashboard/data?${queryString}`, { signal: abortControllerRef.current.signal });

            if (response.ok) {
                setPortfolioData(await response.json());
            } else {
                await handleFetchError(response);
            }
        } catch (error) {
            if (error.name !== 'AbortError') alert(translate('frontend', 'ERROR_INPROCESS'));
        } finally {
            setIsLoading(false);
        }
    }, [setIsLoading]);

    const handlePeriodChange = useCallback((selectedPeriod) => {
        setPeriod(selectedPeriod);
        loadPortfolioData(propertyFilter.properties?.in, selectedPeriod);
    }, [loadPortfolioData, propertyFilter]);

    const handlePropertyFilterChange = useCallback((newFilter) => {
        setPropertyFilter(newFilter);
        loadPortfolioData(newFilter.properties?.in, period);
    }, [loadPortfolioData, period]);

    useEffect(() => {
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        return () => abortController.abort();
    }, []);

    return (
        <>
            <section className="content-header flex justify-between items-center gap-3">
                <h1>{translate('frontend', 'PORTFOLIO_DASHBOARD')}</h1>

                <div>
                    <FilterSelection
                        schema={propertyFilterSchema}
                        filter={propertyFilter}
                        suggestedFieldNames={PROPERTY_FILTER_SUGGESTED_FIELD_NAMES}
                        applyFilters={handlePropertyFilterChange}
                        clearBtn={false}
                        appearance="defaultClear"
                    />
                </div>
            </section>

            <section className="content">
                <PortfolioDashboardProvider period={period}>
                    <PortfolioSummary portfolioSummary={portfolioData.portfolioSummary} />

                    <PortfolioPeriodPicker
                        period={period}
                        onChange={handlePeriodChange}
                    />

                    <div className="grid mb-3">
                        <div className="g-col-12 g-col-lg-6 g-col-xl-9">
                            <PortfolioForecast forecast={portfolioData.forecast} />
                        </div>

                        <div className="g-col-12 g-col-lg-6 g-col-xl-3">
                            <PortfolioAlerts alerts={portfolioData.alerts} />
                        </div>
                    </div>

                    <PortfolioMetrics
                        portfolioMetrics={portfolioData.portfolioMetrics}
                        currency={currency}
                    />

                    <PortfolioHotelRanking
                        hotelRanking={portfolioData.hotelRanking}
                        currency={currency}
                    />
                </PortfolioDashboardProvider>
            </section>
        </>
    );
};

export default PortfolioDashboard;
